import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, ArrowRight, Star, ShieldCheck, Sparkles, Clock, Zap, Sun, Truck, Shield, Droplets, Palette, MapPin, ExternalLink, type LucideIcon } from "lucide-react";
import heroImg from "@/assets/service-hero.jpg";

import heroWebp1600 from "@/assets/project-flake-tan-1600.webp.asset.json";
import heroWebp800 from "@/assets/project-flake-tan-800.webp.asset.json";
import flakeImg from "@/assets/service-flake.jpg";
import metallicImg from "@/assets/service-metallic-v3.jpg";
import commercialImg from "@/assets/service-commercial.jpg";
import grindingImg from "@/assets/service-grinding-v3.jpg";
import resurfacingImg from "@/assets/concrete-resurfacing.jpg.asset.json";
import { SITE, SERVICES, FEATURES, TRUST_POINTS, TESTIMONIALS, CITIES } from "@/lib/site";
import { FAQS, withObjectionFaqs } from "@/lib/faqs";
import { SectionHeading } from "@/components/SectionHeading";
import { ServiceCard } from "@/components/ServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { MultiStepQuoteForm } from "@/components/MultiStepQuoteForm";
import { StatsStrip } from "@/components/StatsStrip";
import { BeforeAfter } from "@/components/BeforeAfter";
import { ProjectsGallery } from "@/components/ProjectsGallery";
import { buildFaqPage, buildBreadcrumbs } from "@/components/SchemaJsonLd";
import { scrollToBelowHeader } from "@/lib/utils";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Calgary Garage Floor Coatings | MLC Epoxy Floors" },
      { name: "description", content: "Premium epoxy and polyaspartic garage floor coatings in Calgary and surrounding areas. Free quotes." },
      { property: "og:title", content: "Calgary Garage Floor Coatings | MLC Epoxy Floors" },
      { property: "og:description", content: "Built To Last. Made To Impress. Diamond-ground epoxy & polyaspartic floor coatings throughout the Calgary region." },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: "https://mlcepoxy.ca/" },
      {
        rel: "preload",
        as: "image",
        href: heroWebp1600.url,
        imageSrcSet: `${heroWebp800.url} 800w, ${heroWebp1600.url} 1600w`,
        imageSizes: "100vw",
        fetchpriority: "high",
      } as any,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildFaqPage(withObjectionFaqs(FAQS.slice(0, 8)))) },
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([{ name: "Home", href: "/" }])) },
    ],
  }),
  component: Home,
});

const SERVICE_IMAGES: Record<string, string> = {
  "flake-epoxy-flooring": flakeImg,
  "metallic-epoxy-flooring": metallicImg,
  "solid-color-epoxy-flooring": commercialImg,
  "polyaspartic-floor-coatings": heroImg,
  "grind-and-seal-concrete": grindingImg,
  "concrete-resurfacing": resurfacingImg.url,
};

const FEATURE_ICONS: Record<string, LucideIcon> = {
  clock: Clock, zap: Zap, sun: Sun, truck: Truck,
  sparkles: Sparkles, shield: Shield, droplets: Droplets, palette: Palette,
};

const HOMEPAGE_STEPS = [
  { n: 1, title: "Free On-Site Quote", body: "We measure, inspect your concrete, and walk you through samples and color options." },
  { n: 2, title: "Diamond Grind & Repairs", body: "Cracks and pitting are filled with structural polyurea, then we mechanically profile the slab — no acid etching, ever." },
  { n: 3, title: "Coat & Polyaspartic Top", body: "Multi-layer epoxy or polyaspartic system finished with a UV-stable top coat for ultimate protection." },
  { n: 4, title: "Enjoy Your New Floor", body: "Walk on it the same day, drive on it the next. Then enjoy it for 20+ years." },
];

export function Home() {
  const [heroFloor, setHeroFloor] = useState<string>("");

  function handleHeroSelect(floor: string) {
    setHeroFloor(floor);
    requestAnimationFrame(() => {
      const formEl = document.getElementById("bottom-quote-form");
      if (formEl) {
        scrollToBelowHeader(formEl);
      }
      const focusable = formEl?.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        focusable.focus({ preventScroll: true });
      }
    });
  }

  return (
    <>
      {/* HERO — split with quote form */}
      <section className="relative isolate overflow-hidden hero-brand-gradient">
        <picture>
          <source type="image/webp" srcSet={`${heroWebp800.url} 800w, ${heroWebp1600.url} 1600w`} sizes="100vw" />
          <img
            src={heroWebp1600.url}
            alt="Flake epoxy garage — Calgary"
            width={1600}
            height={900}
            className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-black/60" aria-hidden />

        <div className="container-x relative pt-20 pb-20 md:pt-24 md:pb-28 lg:pt-28 lg:pb-32">
          <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-copper/50 bg-copper/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-copper">
                <span className="h-1.5 w-1.5 rounded-full bg-copper" /> High-Performance Coatings
              </div>
              <h1 className="mt-5 max-w-3xl text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white text-balance">
                Premium Epoxy Floor Coatings In <span className="text-copper">Calgary</span> &amp; Area
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-white/80">
                Repair and protect your concrete from cracks, scratches, and stains. Industrial-grade epoxy and polyaspartic for garages, basements, showrooms, and commercial spaces — installed in 1-2 days.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href={SITE.phoneHref}
                  className="btn-primary-cta inline-flex items-center justify-center gap-2 rounded-md px-7 py-4 text-sm font-bold uppercase tracking-wider"
                >
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4" fill="#fbbc04" color="#fbbc04" strokeWidth={0} />
                    ))}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-widest text-white">5.0 CUSTOMER REVIEWS</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white">
                  <ShieldCheck className="h-4 w-4 text-copper" /> 10-YEAR WARRANTY
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white">
                  <Sparkles className="h-4 w-4 text-copper" /> Slip-Resistant
                </div>
              </div>
            </div>

            <div id="hero-quote-form" className="lg:pl-4">
              <MultiStepQuoteForm heroMode onHeroSelect={handleHeroSelect} />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <StatsStrip />

      {/* WHY EPOXY */}
      <section className="section-light">
        <div className="container-x py-20 md:py-28">
          <SectionHeading
            eyebrow="Why MLC Epoxy Floors"
            title="No Cracks. No Stains. No Dust."
            body="Every floor we install starts with a permanent mechanical bond and ends with a UV-stable polyaspartic topcoat. We use the same industrial-grade systems specified in dealerships, hangars, and commercial kitchens."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => {
              const Icon = FEATURE_ICONS[f.icon] ?? Sparkles;
              return (
                <div key={f.title} className="rounded-sm bg-ivory border border-border p-7 shadow-sm hover:shadow-md transition-shadow">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-copper/15 border border-copper/30 text-copper">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="mt-5 text-sm font-bold uppercase tracking-widest text-[#121212]">{f.title}</p>
                  <p className="mt-3 text-[#121212]/70 text-sm leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="section-light section-ivory border-y border-border py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="Applications"
            title="Durable Coatings For Every Space."
            body="From two-car garages to industrial warehouses, we install professional-grade systems that look incredible and last for decades."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <ServiceCard
                key={s.slug}
                slug={s.slug}
                name={s.name}
                short={s.short}
                benefits={s.benefits}
                image={SERVICE_IMAGES[s.slug]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <BeforeAfter />

      {/* RECENT PROJECTS */}
      <ProjectsGallery />

      {/* HOW IT WORKS — 4 STEPS */}
      <section className="container-x py-20 md:py-28">
        <SectionHeading
          eyebrow="How It Works"
          title="Four Easy Steps. Zero Stress."
          body="Every MLC Epoxy Floors install follows the same rigorous sequence — the only reason our coatings stay bonded for decades."
        />
        <ol className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {HOMEPAGE_STEPS.map((p) => (
            <li key={p.n} className="relative rounded-sm border border-black/10 bg-white p-6 shadow-sm">
              <span className="absolute -top-4 left-6 inline-flex h-8 w-8 items-center justify-center rounded-sm bg-copper font-display text-sm text-white">
                {p.n.toString().padStart(2, "0")}
              </span>
              <h3 className="mt-3 text-base text-charcoal">{p.title}</h3>
              <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{p.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* REVIEWS — Google-style (unchanged) */}
      <section className="section-light section-ivory border-y border-border">
        <div className="container-x py-20 md:py-28">
          <SectionHeading
            eyebrow="Verified Customer Reviews"
            title="5 Stars Across The Calgary Region"
            body="Real reviews from homeowners and business owners we&rsquo;ve worked with throughout Calgary and surrounding Alberta communities."
          />
          <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#dadce0] bg-white px-5 py-3">
            <span className="text-sm font-medium text-[#3c4043]">Customer Rating</span>
            <span className="text-base font-semibold text-[#202124]">5.0</span>
            <span className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4" fill="#fbbc04" color="#fbbc04" strokeWidth={0} />
              ))}
            </span>
            <span className="text-sm text-[#5f6368]">(127 reviews)</span>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIALS.map((t, idx) => {
              const initial = t.name.trim().charAt(0).toUpperCase();
              const avatarColors = ["#4285f4", "#ea4335", "#fbbc04", "#34a853"];
              const bg = avatarColors[idx % avatarColors.length];
              const reviewCount = [12, 47, 8, 31, 19, 64][idx % 6];
              const when = ["2 weeks ago", "1 month ago", "3 weeks ago", "2 months ago", "5 days ago", "6 weeks ago"][idx % 6];
              return (
                <figure
                  key={t.name}
                  className="rounded-lg border border-[#dadce0] bg-white p-6 shadow-[0_1px_2px_rgba(60,64,67,0.08),0_1px_3px_rgba(60,64,67,0.12)]"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-base text-white"
                      style={{ backgroundColor: bg }}
                      aria-hidden
                    >
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#202124] leading-tight normal-case tracking-normal" style={{ fontFamily: "var(--font-sans)" }}>
                        {t.name}
                      </p>
                      <p className="text-xs text-[#5f6368] mt-0.5">Local Guide · {reviewCount} reviews</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="flex gap-0.5">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <Star key={i} className="h-4 w-4" fill="#fbbc04" color="#fbbc04" strokeWidth={0} />
                      ))}
                    </span>
                    <span className="text-xs text-[#5f6368]">{when}</span>
                  </div>
                  <blockquote className="mt-3 text-[#3c4043] text-sm leading-relaxed">{t.quote}</blockquote>
                  <figcaption className="mt-4 text-xs text-[#5f6368]">{t.city}</figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* FULL QUOTE FORM */}
      <section id="quote" className="relative isolate overflow-hidden bg-jet border-y border-border">
        <div className="absolute inset-0 grain opacity-50" aria-hidden />
        <div className="container-x relative py-20 md:py-28 grid gap-12 lg:grid-cols-[1fr_1.1fr] items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Get Your Free Quote</p>
            <span className="copper-rule mt-4" />
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl text-silver-light text-balance">
              Now Booking Across The Calgary Region.
            </h2>
            <p className="mt-4 text-base md:text-lg text-silver max-w-xl">
              We&rsquo;re filling up fast. Lock in your spot today and start enjoying a cleaner, stronger, more beautiful floor that lasts a lifetime.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "No pressure, no obligation",
                "Free on-site measurement",
                "Detailed written quote in 24 hrs",
                ...TRUST_POINTS.slice(0, 3),
              ].map((p) => (
                <li key={p} className="text-silver text-sm flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-copper shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <a
              id="hero-phone-cta"
              href={SITE.phoneHref}
              className="hero-phone-cta scroll-mt-6 mt-8 inline-flex items-center gap-3 rounded-sm border px-6 py-4 transition-colors"
            >
              <Phone className="h-5 w-5 text-copper" />
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-silver">Call us for a free quote</span>
                <span className="block text-lg font-semibold">{SITE.phone}</span>
              </span>
            </a>
          </div>

          <div id="bottom-quote-form" className="scroll-mt-6 rounded-md border border-border bg-ivory p-7 md:p-8 shadow-2xl">
            <MultiStepQuoteForm initialFloorType={heroFloor} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-light">
        <div className="container-x py-20 md:py-28">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
            <SectionHeading
              eyebrow="Frequently Asked"
              title="Got Questions? We&rsquo;ve Got Answers."
              body="Pricing, longevity, prep, materials — the questions homeowners and contractors ask us most."
            />
            <div>
              <FAQAccordion items={withObjectionFaqs(FAQS.slice(0, 8))} />
              <div className="mt-6">
                <Link to="/faqs" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-copper">
                  View All FAQs <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="section-light section-ivory border-y border-border py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="Service Area"
            title="Based In Airdrie — Serving Calgary & Area"
            body="Headquartered in Airdrie, Alberta. Installing premium epoxy and polyaspartic floor coatings across Calgary and the surrounding communities."
          />
          <div className="mt-12 grid gap-8 lg:grid-cols-1">
            <div className="rounded-sm border border-border bg-jet p-7">
              <p className="text-xs font-bold uppercase tracking-widest text-copper">Calgary & Surrounding Areas</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/service-areas/${c.slug}` as string}
                      className="inline-block rounded-sm border border-border bg-charcoal px-3 py-1.5 text-sm text-silver hover:border-copper hover:text-copper"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 text-center">
            <Link to="/service-areas" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-copper">
              View All Service Areas <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VISIT US — MAP + CONTACT */}
      <section className="bg-jet border-t border-border py-20 md:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Contact Us</p>
            <span className="copper-rule mt-4" />
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {/* Map */}
            <div className="relative overflow-hidden rounded-md border border-border bg-white shadow-xl">
              <a
                href={`https://www.google.com/maps?q=${encodeURIComponent(`${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-charcoal shadow-md ring-1 ring-charcoal/10 hover:text-copper"
              >
                Open in Maps <ExternalLink className="h-3 w-3" />
              </a>
              <iframe
                title="MLC Epoxy Floors location map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(`${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`)}&output=embed`}
                className="h-full min-h-[320px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            {/* Contact card */}
            <div className="rounded-md border border-border bg-white p-8 shadow-xl md:p-10">
              <h3 className="font-display text-2xl font-bold text-charcoal md:text-3xl">{SITE.name}</h3>
              <ul className="mt-6 space-y-5 text-base">
                <li className="flex items-start gap-3 text-charcoal">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-copper" />
                  <span>
                    {SITE.address.street}<br />
                    {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
                  </span>
                </li>
                <li className="flex items-center gap-3 text-charcoal">
                  <Phone className="h-5 w-5 shrink-0 text-copper" />
                  <a href={SITE.phoneHref} className="font-semibold hover:text-copper">{SITE.phone}</a>
                </li>
                <li className="flex items-center gap-3 text-charcoal">
                  <Clock className="h-5 w-5 shrink-0 text-copper" />
                  <span>{SITE.hours}</span>
                </li>
              </ul>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-charcoal px-6 py-3 text-sm font-bold text-white hover:bg-jet"
              >
                View on Google <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
